import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { Text } from '@northladder/i18n';
import { TUIError } from '@northladder/utilities';

import { translations } from '../../../translations';

import { Dropzone } from '../../dropzone';

/**
 * Properties for the UploadFileModalContent component.
 */
interface IUploadFileModalProps {
  /** Callback function to close the modal. */
  onCloseModal: () => void;
  /** Error object if the file upload fails. */
  error: TUIError;
  /** Whether the file is currently being uploaded. */
  isLoading: boolean;
  /** Whether the file has been successfully uploaded. */
  isUploaded: boolean;
  /** Error object to display if a required file is missing. */
  missingFileError: TUIError;
  /** Callback triggered when a file is dropped into the dropzone. */
  onDrop: (file: File[]) => void;
  /** Handler to initiate the file upload process. */
  onHandleFileUpload: () => void;
  /** The currently selected file. */
  selectedFile: File;
  /** Function to set the currently selected file. */
  setSelectedFile: (selectedFile: File | null) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This displays the contents in the modal for uploading a file.
 */
export function UploadFileModalContent({
  isLoading: isUploadingFile,
  error: fileUploadError,
  onCloseModal,
  isUploaded,
  selectedFile,
  setSelectedFile,
  onDrop,
  missingFileError,
  onHandleFileUpload: handleFileUpload,
}: IUploadFileModalProps) {
  const trDocUpload = translations.documentUpload;

  /**
   * Clear the selectedFile and close modal on clicking the `XMarkIcon`.
   */
  const handleClearModal = () => {
    setSelectedFile(null);
    onCloseModal();
  };

  return (
    <div className="w-full">
      <div className="flex justify-end p-2">
        <XMarkIcon className="h-6 w-6" onClick={handleClearModal} />
      </div>

      {isUploaded ? (
        <div className="flex flex-col items-center">
          <CheckCircleIcon className="my-4 h-10 w-10 fill-green-600" />
          <p>
            <Text text={trDocUpload.uploadDone} />
          </p>
          <button
            className="
              focus-visible:(ring-2 ring-offset-2) bg-primary-100 text-primary-900
              ring-primary-500 hover:bg-primary-200 focus:outline-hidden mr-4
              rounded-md border border-transparent px-6 py-2 text-sm font-medium
            "
            onClick={handleClearModal}
            type="button"
          >
            <Text text={trDocUpload.closeButton} />
          </button>
        </div>
      ) : (
        <Dropzone
          error={fileUploadError}
          file={selectedFile}
          isLoading={isUploadingFile}
          missingFileError={missingFileError}
          modalAcceptedFileText={trDocUpload.modalAcceptedFileType}
          onDrop={onDrop}
          onHandleUpload={handleFileUpload}
        />
      )}
    </div>
  );
}
